import { type ReactNode, useEffect, useRef } from 'react';
import { Button } from './Button';

/**
 * Modal primitive aligned to mockup-1 `.modal-overlay` / `.modal` pattern.
 * Sprint 31.7: Updated to use CSS classes from index.css (no Tailwind).
 *
 * - fixed overlay, click outside closes (optional)
 * - ESC closes
 * - focus trap
 * - header/body/footer padding + borders matching mockup
 */

type ModalSize = 'small' | 'medium' | 'large' | 'xlarge';

// Mockup uses 700px as default modal width; align sizes accordingly
const sizePx: Record<ModalSize, number> = {
  small: 480,
  medium: 700,
  large: 800,
  xlarge: 960,
};

export function Modal({
  open,
  title,
  children,
  footer,
  onClose,
  allowBackdropClose = true,
  size = 'large',
  className,
}: {
  open: boolean;
  title: string;
  children: ReactNode;
  footer?: ReactNode;
  onClose: () => void;
  allowBackdropClose?: boolean;
  size?: ModalSize;
  className?: string;
}) {
  const overlayRef = useRef<HTMLDivElement | null>(null);
  const modalRef = useRef<HTMLDivElement | null>(null);
  const prevActive = useRef<HTMLElement | null>(null);

  const width = sizePx[size];

  // Sprint 49: Split into two effects to prevent focus-stealing on re-renders.
  // The keyboard handler depends on [open, onClose] (onClose may change on parent re-render),
  // but auto-focus must only run once when the modal opens (depends on [open] only).
  // Previously, typing in any modal input triggered setFormData -> re-render -> new onClose ref
  // -> useEffect re-ran -> auto-focused first input (username), stealing focus from the current field.

  // Effect 1: Keyboard handlers (ESC close + Tab focus trap) — re-registers when onClose changes
  useEffect(() => {
    if (!open) return;

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
      if (e.key === 'Tab') {
        const root = overlayRef.current;
        if (!root) return;
        const focusables = Array.from(
          root.querySelectorAll<HTMLElement>(
            'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
          )
        ).filter((el) => !el.hasAttribute('disabled') && !el.getAttribute('aria-disabled'));
        if (focusables.length === 0) return;
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        const active = document.activeElement as HTMLElement | null;
        if (!first || !last) return;
        if (e.shiftKey && active === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && active === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open, onClose]);

  // Effect 2: Auto-focus first input on modal open + restore focus on close — depends ONLY on [open]
  // This ensures auto-focus fires exactly once when modal opens, not on every parent re-render.
  useEffect(() => {
    if (!open) return;
    prevActive.current = document.activeElement as HTMLElement | null;

    // Focus first input-ish element after a short delay for DOM to settle
    const t = window.setTimeout(() => {
      const root = overlayRef.current;
      const first = root?.querySelector<HTMLElement>(
        'input, select, textarea, button:not([data-modal-close])'
      );
      if (first) first.focus();
      else modalRef.current?.querySelector<HTMLElement>('button[data-modal-close]')?.focus();
    }, 30);

    return () => {
      window.clearTimeout(t);
      // restore focus to previously active element
      prevActive.current?.focus?.();
      prevActive.current = null;
    };
  }, [open]);

  if (!open) return null;

  // Build modal class list
  const modalClasses = ['modal'];
  if (size === 'small') modalClasses.push('small');
  if (size === 'xlarge') modalClasses.push('large');
  if (className) modalClasses.push(className);

  return (
    <div
      ref={overlayRef}
      className="modal-overlay"
      role="dialog"
      aria-modal="true"
      aria-label={title}
      onMouseDown={(e) => {
        if (!allowBackdropClose) return;
        if (e.target === overlayRef.current) onClose();
      }}
    >
      <div
        ref={modalRef}
        className={modalClasses.join(' ')}
        style={{ width }}
      >
        <header className="modal-header">
          <h3>{title}</h3>
          <Button
            data-modal-close
            variant="secondary"
            size="sm"
            className="modal-close"
            aria-label="Close dialog"
            onClick={onClose}
          >
            ×
          </Button>
        </header>

        <div className="modal-body">
          {children}
        </div>

        {footer ? (
          <footer className="modal-footer">
            {footer}
          </footer>
        ) : null}
      </div>
    </div>
  );
}
